export interface PasswordPolicyComplexity {
  description?: string;
  displayComplexity?: boolean;
  enableMinLength?: boolean;
  minLength?: number;
  needsLowercase?: boolean;
  needsUppercase?: boolean;
  needsNumeric?: boolean;
  needsSymbolic?: boolean;
  disallowSequentialOrRepetitiveChars?: boolean;
  allowUsernameSubstring?: boolean;
  disallowNameSubstring?: boolean;
  enableNameConsecutiveChars?: boolean;
  nameConsecutiveChars?: number;
  firstName?: string;
  lastName?: string;
  username?: string;
}

export interface PasswordRequirementContext {
  firstName?: string;
  lastName?: string;
  username?: string;
  hasDisallowNameInPassword?: boolean;
}

export type RequirementStatus = 'met' | 'unmet' | 'pending';

export interface PasswordRequirementDefinition {
  id: string;
  label: string;
  enabled: boolean;
  validate?: (password: string) => boolean;
}

export interface EvaluatedPasswordRequirement {
  id: string;
  label: string;
  status: RequirementStatus;
}

export const PASSWORD_MAX_LENGTH = 64;
export const PASSWORD_REQUIREMENTS_HEADING = 'Password Requirements (All must be met)';

function inferPasswordPolicyFromDescription(
  description: string,
): Partial<PasswordPolicyComplexity> {
  const trimmed = (description || '').trim();
  if (!trimmed) {
    return {};
  }

  const inferred: Partial<PasswordPolicyComplexity> = {};
  const lengthMatch = trimmed.match(/minimum length of (\d+) characters/i);

  if (lengthMatch) {
    inferred.enableMinLength = true;
    inferred.minLength = Number(lengthMatch[1]);
  }
  if (/lowercase/i.test(trimmed)) {
    inferred.needsLowercase = true;
  }
  if (/uppercase/i.test(trimmed)) {
    inferred.needsUppercase = true;
  }
  if (/\bnumber\b/i.test(trimmed)) {
    inferred.needsNumeric = true;
  }
  if (/special character/i.test(trimmed)) {
    inferred.needsSymbolic = true;
  }
  if (/cannot include user first or last name/i.test(trimmed)) {
    inferred.disallowNameSubstring = true;
  }
  if (/consecutive characters from your first or last name/i.test(trimmed)) {
    inferred.enableNameConsecutiveChars = true;
    const consecutiveMatch = trimmed.match(/cannot include (\d+) or more consecutive/i);
    if (consecutiveMatch) {
      inferred.nameConsecutiveChars = Number(consecutiveMatch[1]);
    }
  }

  return inferred;
}

function hasBaseComplexityRequirements(policy: PasswordPolicyComplexity): boolean {
  return Boolean(
    policy.enableMinLength
    || policy.needsLowercase
    || policy.needsUppercase
    || policy.needsNumeric
    || policy.needsSymbolic
    || policy.disallowSequentialOrRepetitiveChars
    || policy.allowUsernameSubstring === false,
  );
}

function hasStructuredPasswordPolicyFields(policy: PasswordPolicyComplexity): boolean {
  return hasBaseComplexityRequirements(policy);
}

const MIN_NAME_TOKEN_LENGTH = 3;
const NAME_TOKEN_DELIMITERS = /[,.\-_ #\t']+/;

function splitNameTokens(name: string) {
  return String(name ?? '')
    .split(NAME_TOKEN_DELIMITERS)
    .map((token) => token.trim())
    .filter(Boolean);
}

function significantNameTokens(name: string) {
  return splitNameTokens(name).filter((token) => [...token].length >= MIN_NAME_TOKEN_LENGTH);
}

export function containsNameSubstring(password: string, name: string): boolean {
  const lowerPassword = password.toLowerCase();
  return significantNameTokens(name).some((token) => lowerPassword.includes(token.toLowerCase()));
}

function containsConsecutiveFromName(
  lowerPassword: string,
  name: string,
  consecutiveCount: number,
) {
  return significantNameTokens(name).some((token) => {
    const runes = [...token.toLowerCase()];
    if (runes.length < consecutiveCount) {
      return false;
    }
    for (let i = 0; i <= runes.length - consecutiveCount; i += 1) {
      const window = runes.slice(i, i + consecutiveCount).join('');
      if (lowerPassword.includes(window)) {
        return true;
      }
    }
    return false;
  });
}

export function containsNameConsecutiveChars(
  password: string,
  firstName: string,
  lastName: string,
  consecutiveCount: number,
): boolean {
  const count = Number(consecutiveCount);
  if (!count || count < 2) {
    return false;
  }
  const lowerPassword = password.toLowerCase();
  return (
    containsConsecutiveFromName(lowerPassword, firstName, count)
    || containsConsecutiveFromName(lowerPassword, lastName, count)
  );
}

const VALID_SEQUENCE_CHARS = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789';

export function hasRepetitiveOrSequentialPattern(text: string): boolean {
  for (let i = 0; i < text.length - 2; i += 1) {
    const char = text[i];

    if (char === text[i + 1] && char === text[i + 2]) {
      return true;
    }

    if (i + 2 < text.length) {
      const nextChars = text.slice(i, i + 3);
      if (
        VALID_SEQUENCE_CHARS.includes(nextChars[1])
        && VALID_SEQUENCE_CHARS.includes(nextChars[2])
      ) {
        const charCode = char.charCodeAt(0);
        if (
          nextChars === String.fromCharCode(charCode)
            + String.fromCharCode(charCode + 1)
            + String.fromCharCode(charCode + 2)
          || nextChars === String.fromCharCode(charCode)
            + String.fromCharCode(charCode - 1)
            + String.fromCharCode(charCode - 2)
        ) {
          return true;
        }
      }
    }
  }

  return false;
}

export function buildPasswordRequirementContextFromPolicy(
  policy: PasswordPolicyComplexity | null | undefined,
  options: {
    formEmail?: string;
    hasDisallowNameInPassword?: boolean;
  } = {},
): PasswordRequirementContext {
  const emailLocal = (options.formEmail || '').split('@')[0] || '';

  return {
    firstName: policy?.firstName ?? '',
    lastName: policy?.lastName ?? '',
    username: policy?.username || emailLocal,
    hasDisallowNameInPassword: options.hasDisallowNameInPassword,
  };
}

export function buildPasswordRequirements(
  policy: PasswordPolicyComplexity,
  context: PasswordRequirementContext = {},
): PasswordRequirementDefinition[] {
  const requirements: PasswordRequirementDefinition[] = [{
    id: 'minLength',
    label: `At least ${policy.minLength} characters`,
    enabled: Boolean(policy.enableMinLength),
    validate(password) {
      return password.length >= (policy.minLength || 0);
    },
  }, {
    id: 'lowercase',
    label: 'A lowercase letter',
    enabled: Boolean(policy.needsLowercase),
    validate(password) {
      return /[a-z]/.test(password);
    },
  }, {
    id: 'uppercase',
    label: 'An uppercase letter',
    enabled: Boolean(policy.needsUppercase),
    validate(password) {
      return /[A-Z]/.test(password);
    },
  }, {
    id: 'number',
    label: 'A number',
    enabled: Boolean(policy.needsNumeric),
    validate(password) {
      return /[0-9]/.test(password);
    },
  }, {
    id: 'specialChar',
    label: 'A special character (!, @, #, etc.)',
    enabled: Boolean(policy.needsSymbolic),
    validate(password) {
      return /[\W_]/.test(password);
    },
  }, {
    id: 'repetitiveOrSequential',
    label: 'Cannot contain repeated or sequential characters',
    enabled: Boolean(policy.disallowSequentialOrRepetitiveChars),
    validate(password) {
      return !hasRepetitiveOrSequentialPattern(password);
    },
  }];

  if (policy.allowUsernameSubstring === false) {
    const username = String(context.username ?? '').trim();
    requirements.push({
      id: 'noUsername',
      label: 'Cannot contain your username',
      enabled: true,
      validate(password) {
        if (username.length === 0) {
          return true;
        }
        return !password.toLowerCase().includes(username.toLowerCase());
      },
    });
  }

  if (
    context.hasDisallowNameInPassword
    && hasBaseComplexityRequirements(policy)
    && policy.disallowNameSubstring
  ) {
    requirements.push({
      id: 'noNameSubstring',
      label: 'Cannot contain your first or last name',
      enabled: true,
      validate(password) {
        return (
          !containsNameSubstring(password, context.firstName ?? '')
          && !containsNameSubstring(password, context.lastName ?? '')
        );
      },
    });
  }

  if (
    context.hasDisallowNameInPassword
    && hasBaseComplexityRequirements(policy)
    && policy.enableNameConsecutiveChars
  ) {
    const consecutiveCount = policy.nameConsecutiveChars || 2;
    requirements.push({
      id: 'noNameConsecutive',
      label: `Cannot contain ${consecutiveCount} or more consecutive characters from your first or last name`,
      enabled: true,
      validate(password) {
        return !containsNameConsecutiveChars(
          password,
          context.firstName ?? '',
          context.lastName ?? '',
          consecutiveCount,
        );
      },
    });
  }

  return requirements.filter((requirement) => requirement.enabled);
}

export function shouldShowPasswordRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
  context: PasswordRequirementContext = {},
): boolean {
  if (!policy || policy.displayComplexity === false) {
    return false;
  }

  return buildPasswordRequirements(policy, context).length > 0;
}

export function evaluatePasswordRequirements(
  password: string,
  requirements: PasswordRequirementDefinition[],
): EvaluatedPasswordRequirement[] {
  const isEmpty = password.length === 0;

  return requirements.map((requirement) => {
    if (!requirement.validate || isEmpty) {
      return {
        id: requirement.id,
        label: requirement.label,
        status: 'pending',
      };
    }

    return {
      id: requirement.id,
      label: requirement.label,
      status: requirement.validate(password) ? 'met' : 'unmet',
    };
  });
}

export function getEvaluatedPasswordRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
  password: string,
  context: PasswordRequirementContext = {},
): EvaluatedPasswordRequirement[] {
  if (!shouldShowPasswordRequirements(policy, context)) {
    return [];
  }

  const requirements = buildPasswordRequirements(policy as PasswordPolicyComplexity, context);
  return evaluatePasswordRequirements(password, requirements);
}

export function isPasswordSatisfyingClientCheckableRequirements(
  policy: PasswordPolicyComplexity | null | undefined,
  password: string,
  context: PasswordRequirementContext = {},
): boolean {
  const evaluated = getEvaluatedPasswordRequirements(policy, password, context);
  return evaluated.every((item) => item.status === 'met');
}

function mergeInferredPasswordRulesFromDescription(
  normalized: PasswordPolicyComplexity,
  description: string,
): PasswordPolicyComplexity {
  const inferred = inferPasswordPolicyFromDescription(description);

  return {
    ...normalized,
    ...(inferred.disallowNameSubstring && !normalized.disallowNameSubstring
      ? { disallowNameSubstring: true }
      : {}),
    ...(inferred.enableNameConsecutiveChars && !normalized.enableNameConsecutiveChars
      ? {
        enableNameConsecutiveChars: true,
        ...(inferred.nameConsecutiveChars
          ? { nameConsecutiveChars: inferred.nameConsecutiveChars }
          : {}),
      }
      : {}),
    ...(inferred.disallowSequentialOrRepetitiveChars
      && !normalized.disallowSequentialOrRepetitiveChars
      ? { disallowSequentialOrRepetitiveChars: true }
      : {}),
  };
}

export function normalizePasswordPolicyResponse(
  response: PasswordPolicyComplexity | string | null | undefined,
): PasswordPolicyComplexity | null {
  if (!response) {
    return null;
  }

  if (typeof response === 'string') {
    const description = response.trim();
    if (!description) {
      return null;
    }

    return {
      description,
      displayComplexity: true,
      ...inferPasswordPolicyFromDescription(description),
    };
  }

  if (response.displayComplexity === false) {
    return null;
  }

  const description = (response.description || '').trim();
  let normalized: PasswordPolicyComplexity = {
    description: response.description,
    displayComplexity: true,
    enableMinLength: response.enableMinLength,
    minLength: response.minLength,
    needsLowercase: response.needsLowercase,
    needsUppercase: response.needsUppercase,
    needsNumeric: response.needsNumeric,
    needsSymbolic: response.needsSymbolic,
    disallowSequentialOrRepetitiveChars: response.disallowSequentialOrRepetitiveChars,
    allowUsernameSubstring: response.allowUsernameSubstring,
    disallowNameSubstring: response.disallowNameSubstring,
    enableNameConsecutiveChars: response.enableNameConsecutiveChars,
    nameConsecutiveChars: response.nameConsecutiveChars,
    firstName: response.firstName,
    lastName: response.lastName,
    username: response.username,
  };

  if (!hasStructuredPasswordPolicyFields(normalized) && description) {
    Object.assign(normalized, inferPasswordPolicyFromDescription(description));
  } else if (description) {
    normalized = mergeInferredPasswordRulesFromDescription(normalized, description);
  }

  if (!hasStructuredPasswordPolicyFields(normalized) && !description) {
    return null;
  }

  return normalized;
}
